/**
 * Enhanced Variants Handle
 * Handles variant filtering by country, selector DOM management, and product updates
 * on variant change. Relies on globals set by the enhanced-product-information liquid
 * inline script: window.productVariants, window.productOptionNames,
 * window.enhancedProductTranslations, window.enhancedCurrencyCode,
 * window.variantTaxData, window.variantTranslations, window.variantMetafields
 */

// ─── Locale/Country helpers ───────────────────────────────────────────────────

/**
 * Maps country codes to their proper formatting locales.
 * EU countries use comma as decimal separator (fr-FR, de-DE, etc.).
 * Using the URL locale directly (e.g. en-fr) would force dot separators.
 */
const PRICE_LOCALE_MAP = {
  FR: 'fr-FR', IT: 'it-IT', AT: 'de-AT', BE: 'fr-BE', DE: 'de-DE',
  CH: 'de-CH', NL: 'nl-NL', ES: 'es-ES', GB: 'en-GB', TR: 'tr-TR',
  CA: 'en-CA', US: 'en-US', PR: 'en-US',
};

/**
 * Gets the target country from DMO data or falls back to URL locale.
 * Returns uppercase country code (US, CA, PR).
 */
function getTargetCountry() {
  if (window.data?.profile?.targetCountry) {
    return window.data.profile.targetCountry.toUpperCase();
  }
  const currentLocale = (typeof window.getCurrentLocale === 'function')
    ? window.getCurrentLocale()
    : 'en-US';
  const country = currentLocale.split('-')[1]?.toUpperCase();
  return country || 'US';
}

// ─── System 1: DOM-manipulation-based filtering (runs immediately on load) ────

/**
 * Filters window.productVariants by target country SKU suffix (_us, _ca, _pr).
 * Returns all variants as fallback if none match.
 */
function getVariantsForCountry(targetCountry) {
  if (!window.productVariants || !Array.isArray(window.productVariants)) return [];
  const suffix = `_${targetCountry.toLowerCase()}`;
  return window.productVariants.filter(v => v.sku && v.sku.toLowerCase().endsWith(suffix) && v.is_active !== false);
}

/**
 * Returns the available option values for a given option position,
 * respecting the current selections on the other axes.
 */
function getAvailableValuesForOption(optionPosition, currentSelections = {}) {
  const countryVariants = getVariantsForCountry(getTargetCountry());
  const filtered = countryVariants.filter(v => {
    for (const [pos, value] of Object.entries(currentSelections)) {
      if (parseInt(pos) === optionPosition) continue;
      if (v[`option${pos}`] !== value) return false;
    }
    return true;
  });
  const values = new Set();
  filtered.forEach(v => { if (v[`option${optionPosition}`]) values.add(v[`option${optionPosition}`]); });
  return Array.from(values);
}

/**
 * Finds a variant from window.productVariants matching the given selections map
 * { [optionPosition]: value }.
 */
function findMatchingVariantByPosition(selections) {
  const countryVariants = getVariantsForCountry(getTargetCountry());
  return countryVariants.find(v =>
    Object.entries(selections).every(([pos, val]) => v[`option${pos}`] === val)
  ) || null;
}

/** Stores original <option> elements for a <select> before any DOM modification. */
const originalSelectOptions = new Map();
function storeOriginalOptions(select) {
  if (!originalSelectOptions.has(select)) {
    originalSelectOptions.set(select, Array.from(select.options).map(o => ({
      value: o.value,
      text: o.textContent,
      selected: o.selected
    })));
  }
}

/**
 * Rebuilds a single <select> to show only the available values for its option
 * position, auto-selecting the first available item when the current value
 * is no longer valid.
 */
function updateSingleSelector(selectToUpdate, currentSelections, autoSelect = false) {
  const position = parseInt(selectToUpdate.dataset.optionPosition);
  const currentValue = selectToUpdate.value;
  storeOriginalOptions(selectToUpdate);
  const availableValues = getAvailableValuesForOption(position, currentSelections);
  const originalOptions = originalSelectOptions.get(selectToUpdate);
  selectToUpdate.innerHTML = '';
  let currentValueExists = false;
  let firstAvailable = null;
  const addedValues1 = new Set();
  originalOptions.forEach(od => {
    if (!availableValues.includes(od.value)) return;
    if (addedValues1.has(od.value)) return;
    addedValues1.add(od.value);
    const opt = document.createElement('option');
    opt.value = od.value;
    opt.textContent = od.text;
    if (od.value === currentValue) { opt.selected = true; currentValueExists = true; }
    selectToUpdate.appendChild(opt);
    if (firstAvailable === null) firstAvailable = od.value;
  });
  if (!currentValueExists && autoSelect && firstAvailable) {
    selectToUpdate.value = firstAvailable;
    return true;
  }
  return false;
}

/**
 * Updates all <select> elements with a higher option-position than the changed
 * one (or all from position 2 up on initial load).
 */
function updateVariantSelectors(changedSelect = null) {
  const variantSelector = document.querySelector('[data-variant-selector]');
  if (!variantSelector) return;
  const selects = variantSelector.querySelectorAll('.variant-select');
  if (!selects.length) return;
  const currentSelections = {};
  selects.forEach(s => {
    const pos = parseInt(s.dataset.optionPosition);
    if (s.value) currentSelections[pos] = s.value;
  });
  const changedPos = changedSelect ? parseInt(changedSelect.dataset.optionPosition) : 0;
  selects.forEach(select => {
    const pos = parseInt(select.dataset.optionPosition);
    if (changedSelect && select === changedSelect) return;
    if (changedPos === 0 ? pos === 1 : pos <= changedPos) return;
    const changed = updateSingleSelector(select, currentSelections, true);
    if (changed) select.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/** Rebuilds the first <select> to show only values available in the target country. */
function filterInitialOptions() {
  const variantSelector = document.querySelector('[data-variant-selector]');
  if (!variantSelector) return;
  const selects = variantSelector.querySelectorAll('.variant-select');
  if (!selects.length) return;
  const targetCountry = getTargetCountry();
  const countryVariants = getVariantsForCountry(targetCountry);
  if (!countryVariants.length) return;
  const firstSelect = Array.from(selects).find(s => parseInt(s.dataset.optionPosition) === 1);
  if (!firstSelect) return;
  storeOriginalOptions(firstSelect);
  const position = parseInt(firstSelect.dataset.optionPosition);
  const availableValues = new Set();
  countryVariants.forEach(v => { if (v[`option${position}`]) availableValues.add(v[`option${position}`]); });
  const origOptions = originalSelectOptions.get(firstSelect);
  const currentValue = firstSelect.value;
  firstSelect.innerHTML = '';
  let hasCurrentValue = false;
  let firstAvailable = null;
  const addedValues2 = new Set();
  origOptions.forEach(od => {
    if (!availableValues.has(od.value)) return;
    if (addedValues2.has(od.value)) return;
    addedValues2.add(od.value);
    const opt = document.createElement('option');
    opt.value = od.value;
    opt.textContent = od.text;
    if (od.value === currentValue) { opt.selected = true; hasCurrentValue = true; }
    firstSelect.appendChild(opt);
    if (firstAvailable === null) firstAvailable = od.value;
  });
  if (!hasCurrentValue && firstAvailable) firstSelect.value = firstAvailable;
  selects.forEach(s => { if (s !== firstSelect) storeOriginalOptions(s); });
}

/** Ensures the current select values correspond to a valid country variant. */
function setInitialVariantSelection() {
  const variantSelector = document.querySelector('[data-variant-selector]');
  if (!variantSelector) return;
  const selects = variantSelector.querySelectorAll('.variant-select');
  if (!selects.length) return;
  const countryVariants = getVariantsForCountry(getTargetCountry());
  if (!countryVariants.length) return;
  const currentSelections = {};
  selects.forEach(s => { currentSelections[parseInt(s.dataset.optionPosition)] = s.value; });
  if (!findMatchingVariantByPosition(currentSelections)) {
    const first = countryVariants[0];
    selects.forEach(s => {
      const pos = parseInt(s.dataset.optionPosition);
      const val = first[`option${pos}`];
      if (val && s.value !== val) s.value = val;
    });
  }
}

/** Runs all System 1 filtering steps on initial page load. */
function initializeVariantFiltering() {
  const variantSelector = document.querySelector('[data-variant-selector]');
  if (!variantSelector) return;
  if (!variantSelector.querySelectorAll('.variant-select').length) return;
  filterInitialOptions();
  setInitialVariantSelection();
  updateVariantSelectors(null);
  variantSelector.querySelectorAll('.variant-select').forEach(select => {
    select.addEventListener('change', function () { updateVariantSelectors(this); });
  });
}

// ─── System 2: event-based filtering (waits for DMO data) ────────────────────

/**
 * Filters variants using getTargetCountry() (always has a fallback).
 * Matches by SKU suffix (e.g. _us, _ca, _pr).
 */
function filterVariantsByTargetCountry(variants) {
  const targetCountryLower = getTargetCountry().toLowerCase();
  const filtered = variants.filter(v => {
    if (!v.sku || v.is_active === false) return false;
    const parts = v.sku.split('_');
    if (parts.length < 2) return false;
    return parts[parts.length - 1].toLowerCase() === targetCountryLower;
  });
  return filtered.length ? filtered : variants;
}

/**
 * Waits for DMO data via the dmo:data:loaded event (dispatched by main.ts).
 * Falls back to URL locale via getTargetCountry() after 5 s.
 */
function waitForDMODataAndFilter(variants, variantSelects, callback) {
  if (window.data?.profile?.targetCountry) {
    callback(filterVariantsByTargetCountry(variants));
    return;
  }
  let handled = false;
  function runFilter() {
    if (handled) return;
    handled = true;
    document.removeEventListener('dmo:data:loaded', onDataLoaded);
    clearTimeout(fallbackTimer);
    callback(filterVariantsByTargetCountry(variants));
  }
  function onDataLoaded() { runFilter(); }
  document.addEventListener('dmo:data:loaded', onDataLoaded, { once: true });
  const fallbackTimer = setTimeout(runFilter, 5000);
}

/**
 * Hides/shows <option> elements based on the filtered variant set.
 * Restores original options first so country switches work correctly.
 * Uses option.hidden for cross-browser compatibility (display:none is ignored in Firefox/Safari).
 */
function applyVariantFilters(variantSelects, filteredVariants) {
  const filteredOptionValues = new Map();
  filteredVariants.forEach(variant => {
    (variant.options || []).forEach((val, i) => {
      const pos = i + 1;
      if (!filteredOptionValues.has(pos)) filteredOptionValues.set(pos, new Set());
      filteredOptionValues.get(pos).add(val);
    });
  });
  variantSelects.forEach(select => {
    const pos = parseInt(select.dataset.optionPosition);
    const allowed = filteredOptionValues.get(pos);
    if (!allowed) return;
    if (originalSelectOptions.has(select)) {
      const cur = select.value;
      select.innerHTML = '';
      const addedValues3 = new Set();
      originalSelectOptions.get(select).forEach(od => {
        if (addedValues3.has(od.value)) return;
        addedValues3.add(od.value);
        const opt = document.createElement('option');
        opt.value = od.value;
        opt.textContent = od.text;
        if (od.value === cur) opt.selected = true;
        select.appendChild(opt);
      });
    }
    let firstVisible = null;
    const currentValue = select.value;
    Array.from(select.options).forEach(opt => {
      if (!allowed.has(opt.value)) {
        opt.remove();
      } else {
        if (!firstVisible) firstVisible = opt;
      }
    });
    if (!Array.from(select.options).some(o => o.value === currentValue) && firstVisible) {
      select.value = firstVisible.value;
    }
  });
  document.dispatchEvent(new CustomEvent('variant:filters:applied'));
}

// ─── Variant change handling ──────────────────────────────────────────────────

/** Removes the last _xx country suffix from a SKU string. */
function stripSkuCountrySuffix(sku) {
  if (!sku) return '';
  const parts = sku.split('_');
  if (parts.length > 1) { parts.pop(); return parts.join('_'); }
  return sku;
}

/**
 * Finds a variant from an array by matching option names to selected values.
 * Uses window.productOptionNames for the option-position → name mapping.
 */
function findVariantByOptions(variants, selectedOptions) {
  const optionNames = window.productOptionNames || [];
  return variants.find(variant =>
    (variant.options || []).every((optionValue, index) => {
      const name = optionNames[index];
      if (!name || !Object.prototype.hasOwnProperty.call(selectedOptions, name)) return true;
      return selectedOptions[name] === optionValue;
    })
  );
}

/**
 * Formats a cent amount to a locale-aware currency string.
 * Uses window.enhancedCurrencyCode and country-based locale for correct decimal separators.
 */
function formatMoney(cents) {
  const dollars = Math.floor(cents) / 100;
  const currency = window.enhancedCurrencyCode || 'USD';
  const country = getTargetCountry();
  const locale = PRICE_LOCALE_MAP[country] || 'en-US';
  return dollars.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Formats a dollar amount directly as a locale-aware currency string.
 * Use for backend catalog prices (CatalogPriceEntry) which are already in display units.
 * No cents conversion — backend values are authoritative.
 * Uses country-based locale (fr-FR, de-DE) so EU prices display with comma decimal separator.
 */
function formatDollars(amount) {
  const currency = window.enhancedCurrencyCode || 'USD';
  const country = getTargetCountry();
  const locale = PRICE_LOCALE_MAP[country] || 'en-US';
  return amount.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Updates all product page UI elements when the user selects a different variant:
 * translations, tab content, SKU, availability, URL, and all price surfaces.
 */
function updateProductWithVariant(variant) {
  window.selectedVariantId = variant.id;
  const variantKey = String(variant.id);

  // Translations
  if (window.variantTranslations?.[variantKey]) {
    window.productTranslations = window.variantTranslations[variantKey];
    if (typeof window.applyProductTranslations === 'function') window.applyProductTranslations();
  } else {
    window.productTranslations = {};
  }

  // Tab content (metafields)
  if (window.variantMetafields?.[variantKey]) {
    window.currentVariantMetafields = window.variantMetafields[variantKey];
    if (typeof window.applyProductTabTranslations === 'function') window.applyProductTabTranslations();
  } else {
    window.currentVariantMetafields = {};
  }

  // SKU
  const skuEl = document.querySelector('[data-product-target="sku"]');
  if (skuEl) {
    if (variant.sku) {
      const label = window.enhancedProductTranslations?.sku_label || 'SKU';
      skuEl.textContent = `${label}: ${stripSkuCountrySuffix(variant.sku)}`;
      skuEl.style.display = 'block';
    } else {
      skuEl.style.display = 'none';
    }
  }

  // Availability
  const stockEl = document.querySelector('.stock-status');
  if (stockEl) {
    const inStock = window.enhancedProductTranslations?.in_stock || 'In stock, ready to ship';
    const outOfStock = window.enhancedProductTranslations?.out_of_stock || 'Out of stock';
    stockEl.textContent = variant.available ? inStock : outOfStock;
  }

  // Add-to-cart button
  const addToCartBtn = document.querySelector('[data-product-add-to-cart]');
  if (addToCartBtn) addToCartBtn.disabled = !variant.available;

  // URL
  const url = new URL(window.location.href);
  url.searchParams.set('variant', variant.id);
  history.replaceState(null, '', url.href);

  // Prices — use catalog_prices metafield values (dollars) for international markets when available,
  // otherwise fall back to Shopify variant price + tax rate metafield.
  const taxRate     = window.variantTaxData?.[variantKey] ?? 0;
  const catalogPrices = window.variantCatalogPrices?.[variantKey];

  const priceAmountEl  = document.querySelector('[data-product-target="price"] .price-amount');
  const salePriceEl    = document.querySelector('.subscription-option.subscribe-save .option-price .sale-price');
  const originalPriceEl= document.querySelector('.subscription-option.subscribe-save .option-price .original-price');
  const regularPriceEl = document.querySelector('.subscription-option.one-time .option-price .regular-price');
  const subscribeCartEl= document.querySelector('.subscribe-text .cart-price');
  const onetimeCartEl  = document.querySelector('.onetime-text .cart-price');
  const exclVatLabel   = window.contentTranslations?.excl_vat_label || 'Excl. VAT';
  const headerExclEl      = document.querySelector('[data-excl-vat-target="header"]');
  const subExclEl         = document.querySelector('[data-excl-vat-target="subscription"]');
  const subOriginalExclEl = document.querySelector('[data-excl-vat-target="subscription-original"]');
  const onetimeExclEl     = document.querySelector('[data-excl-vat-target="onetime"]');

  if (catalogPrices) {
    // International: display backend CatalogPriceEntry values directly — no conversion or calculation.
    // Backend is the authoritative source; these values are already in display units (dollars/euros/etc.).
    if (priceAmountEl)   priceAmountEl.textContent   = formatDollars(catalogPrices.VATPrice);
    if (salePriceEl)     { salePriceEl.textContent    = formatDollars(catalogPrices.VATSubscriptionPrice); salePriceEl.setAttribute('data-subscription-price', String(catalogPrices.VATSubscriptionPrice)); }
    if (originalPriceEl) { originalPriceEl.textContent = formatDollars(catalogPrices.VATPrice); originalPriceEl.setAttribute('data-original-price', String(catalogPrices.VATPrice)); }
    if (regularPriceEl)  regularPriceEl.textContent  = formatDollars(catalogPrices.VATPrice);
    if (subscribeCartEl) subscribeCartEl.textContent  = formatDollars(catalogPrices.VATSubscriptionPrice);
    if (onetimeCartEl)   onetimeCartEl.textContent    = formatDollars(catalogPrices.VATPrice);

    if (headerExclEl)      { headerExclEl.textContent      = `${formatDollars(catalogPrices.RetailPrice)} ${exclVatLabel}`;       headerExclEl.style.display      = ''; }
    if (subExclEl)         { subExclEl.textContent         = `${formatDollars(catalogPrices.SubscriptionPrice)} ${exclVatLabel}`; subExclEl.style.display         = ''; }
    if (subOriginalExclEl) { subOriginalExclEl.textContent = `${formatDollars(catalogPrices.RetailPrice)} ${exclVatLabel}`;       subOriginalExclEl.style.display = ''; }
    if (onetimeExclEl)     { onetimeExclEl.textContent     = `${formatDollars(catalogPrices.RetailPrice)} ${exclVatLabel}`;       onetimeExclEl.style.display     = ''; }
  } else {
    // US/CA or no catalog price: Shopify variant price (cents) + tax rate metafield.
    const taxMult     = 1 + Number(taxRate);
    const baseCents   = Math.floor(variant.price * taxMult);
    const subCents    = Math.floor(variant.price * 0.95 * taxMult);
    const exclBaseCents = variant.price;
    const exclSubCents  = Math.floor(variant.price * 0.95);
    const hasVat      = taxMult > 1;

    if (priceAmountEl)   priceAmountEl.textContent   = formatMoney(baseCents);
    if (salePriceEl)     { salePriceEl.textContent    = formatMoney(subCents); salePriceEl.setAttribute('data-subscription-price', String(subCents)); }
    if (originalPriceEl) { originalPriceEl.textContent = formatMoney(baseCents); originalPriceEl.setAttribute('data-original-price', String(baseCents)); }
    if (regularPriceEl)  regularPriceEl.textContent  = formatMoney(baseCents);
    if (subscribeCartEl) subscribeCartEl.textContent  = formatMoney(subCents);
    if (onetimeCartEl)   onetimeCartEl.textContent    = formatMoney(baseCents);

    if (hasVat) {
      const baseExclText = `${formatMoney(exclBaseCents)} ${exclVatLabel}`;
      const subExclText  = `${formatMoney(exclSubCents)} ${exclVatLabel}`;
      if (headerExclEl)      { headerExclEl.textContent      = baseExclText; headerExclEl.style.display      = ''; }
      if (subExclEl)         { subExclEl.textContent          = subExclText;  subExclEl.style.display         = ''; }
      if (subOriginalExclEl) { subOriginalExclEl.textContent  = baseExclText; subOriginalExclEl.style.display = ''; }
      if (onetimeExclEl)     { onetimeExclEl.textContent      = baseExclText; onetimeExclEl.style.display     = ''; }
    } else {
      if (headerExclEl)      headerExclEl.style.display      = 'none';
      if (subExclEl)         subExclEl.style.display          = 'none';
      if (subOriginalExclEl) subOriginalExclEl.style.display  = 'none';
      if (onetimeExclEl)     onetimeExclEl.style.display      = 'none';
    }
  }

  document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
}

// ─── Main setup ───────────────────────────────────────────────────────────────

/**
 * Sets up all variant <select> elements on product pages:
 * waits for DMO data, applies country filter, wires up change listeners.
 * Called by initializeEnhancedProductInteractions() in the liquid inline script.
 */
function setupVariantSelectorHandlers() {
  const variantSelector = document.querySelector('[data-variant-selector]');
  if (!variantSelector) return;

  // Only runs on product pages (window.productVariants populated by Liquid)
  if (!window.productVariants?.length || !window.productOptionNames) return;

  const variants = window.productVariants;
  const variantSelects = variantSelector.querySelectorAll('.variant-select');

  let selectedOptions = {};
  variantSelects.forEach(select => {
    selectedOptions[select.dataset.optionName] = select.value;
  });

  waitForDMODataAndFilter(variants, variantSelects, (filteredVariants) => {
    applyVariantFilters(variantSelects, filteredVariants);
    // Re-cascade dependent selectors (position 2+) so they only show sizes
    // valid for the currently selected flavor — applyVariantFilters filters
    // by country only and would otherwise show sizes that belong to other flavours.
    updateVariantSelectors(null);

    variantSelects.forEach(select => {
      selectedOptions[select.dataset.optionName] = select.value;
    });

    const initialVariant = findVariantByOptions(filteredVariants, selectedOptions);
    if (initialVariant) {
      window.selectedVariantId = initialVariant.id;
      updateProductWithVariant(initialVariant);
    }

    variantSelects.forEach(select => {
      select.addEventListener('change', function () {
        selectedOptions[this.dataset.optionName] = this.value;
        const match = findVariantByOptions(filteredVariants, selectedOptions);
        if (match) updateProductWithVariant(match);
      });
    });
  });
}

// ─── Subscription state ───────────────────────────────────────────────────────

/**
 * When subscriptionsEnabled === false, hides the subscribe-save option and
 * ensures the one-time option is selected and active.
 */
function applySubscriptionEnabledState() {
  const subscribeSaveEls = document.querySelectorAll('.subscription-option.subscribe-save');
  if (window.data?.subscriptionsEnabled === false) {
    subscribeSaveEls.forEach(el => el.classList.remove('subs-visible'));
    const subscribeRadio = document.querySelector('#subscribe');
    if (subscribeRadio) subscribeRadio.checked = false;
    const onetimeRadio = document.querySelector('#one-time');
    if (onetimeRadio && !onetimeRadio.checked) {
      onetimeRadio.checked = true;
      onetimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    document.querySelectorAll('.subscription-option.one-time').forEach(el => {
      el.classList.add('active');
    });
  } else {
    // Subscriptions enabled — add visible class to show
    subscribeSaveEls.forEach(el => {
      el.classList.add('subs-visible');
      el.removeAttribute('data-subscriptions-loading');
    });
  }
}

// ─── Initialization ───────────────────────────────────────────────────────────

// System 1: run immediately (defer scripts execute before DOMContentLoaded)
initializeVariantFiltering();

// Apply subscription state once DMO data is available
if (window.data?.subscriptionsEnabled !== undefined) {
  applySubscriptionEnabledState();
} else {
  document.addEventListener('dmo:data:loaded', applySubscriptionEnabledState, { once: true });
}

// Expose setupVariantSelectorHandlers globally so the liquid inline script
// can call it from initializeEnhancedProductInteractions()
window.setupVariantSelectorHandlers = setupVariantSelectorHandlers;
